import { css, Theme } from '@emotion/react';
import { Typography } from '@mui/material';
import React, { Fragment } from 'react';
import { useLocation } from 'react-router-dom';

import { shouldBePassword } from '@jebel/utils';

import { RouteLayout, Box, Form, FormTextField, Button, RouterLink } from 'shared/components/ui';
import { APP_URL } from 'shared/constants';
import { isValidEmail } from 'shared/utils/form';

import { FormError } from '../form';

const layoutContainerCSS = css`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  flex-shrink: 1;
  flex-basis: 0;
  height: 100%;
`;

const topBoxCSS = css`
  display: grid;
  grid-row-gap: 20px;
`;

const titleCSS = (theme: Theme) => css`
  line-height: 26px;
  font-size: ${theme.typography.fontSize + 6}px;
  font-weight: 600;
  width: 100%;
  padding: ${theme.spacing(1.25)}px;
  text-align: center;
`;

const formBoxCSS = css`
  display: grid;
  grid-gap: 20px;
`;

const buttonsBoxCSS = css`
  display: grid;
  grid-gap: 30px;
`;

const returnBoxCSS = css`
  display: grid;
  grid-template-columns: auto auto;
  place-content: center;
  place-items: center;
`;

const loginLinkCSS = (theme: Theme) => css`
  white-space: nowrap;
  color: ${theme.palette.primary.main};
`;

export type FormDataRecovery = {
  email?: string;
};

export type FormDataReset = {
  password?: string;
  confirmPassword?: string;
};

interface ResetPageProps {
  onSubmit: <T extends FormDataRecovery & FormDataReset>(data: T) => void;
  resetError?: string;
}

export const ResetLayout: React.FC<ResetPageProps> = ({ onSubmit, resetError }) => {
  const location = useLocation();
  const isRecoveryRoute = location.pathname === APP_URL.public.recovery;
  const pageName = isRecoveryRoute ? 'recovery' : 'reset';
  const title = pageName === 'recovery' ? 'Reset Your Password' : 'Change Your Password';
  const submitTitle = pageName === 'recovery' ? 'Reset Password' : 'Change Password';

  const renderFields = () => {
    let content = (
      <FormTextField
        inputProps={{
          color: 'primary',
          label: 'Enter your Email Address',
          variant: 'outlined',
        }}
        fieldProps={{
          name: 'email',
          validate: isValidEmail,
        }}
      />
    );
    if (pageName === 'reset') {
      content = (
        <React.Fragment>
          <FormTextField
            inputProps={{
              color: 'primary',
              label: 'Enter new password',
              variant: 'outlined',
              type: 'password',
            }}
            fieldProps={{
              name: 'password',
              validate: shouldBePassword,
            }}
          />
          <FormTextField
            inputProps={{
              color: 'primary',
              label: 'Commit new password',
              variant: 'outlined',
              type: 'password',
            }}
            fieldProps={{
              name: 'confirmPassword',
              validate: shouldBePassword,
            }}
          />
        </React.Fragment>
      );
    }

    return content;
  };

  return (
    <RouteLayout css={layoutContainerCSS}>
      <Box css={topBoxCSS}>
        <Typography
          css={titleCSS}
          variant="inherit"
          align="inherit"
          color="initial"
          paragraph={false}
        >
          {title}
        </Typography>
        {resetError && <FormError text={resetError} />}
        <Form oldCss={formBoxCSS} onSubmit={onSubmit}>
          {({ isSubmitting }) => (
            <Fragment>
              {renderFields()}

              <Box css={buttonsBoxCSS}>
                <Button
                  loading={isSubmitting}
                  size="large"
                  variant="contained"
                  color="primary"
                  type="submit"
                >
                  {submitTitle}
                </Button>

                <Box css={returnBoxCSS}>
                  <RouterLink
                    css={loginLinkCSS}
                    absolute={false}
                    color="primary"
                    to={APP_URL.public.login}
                  >
                    Return to Login
                  </RouterLink>
                </Box>
              </Box>
            </Fragment>
          )}
        </Form>
      </Box>
    </RouteLayout>
  );
};
